/**
 * IconButton — bordered icon-only button with variant colouring.
 *
 * Size tiers (aligned with the design-system height standard):
 *   sm  → 32px (w-8 h-8)   — micro: chip dismiss, tiny utility
 *   md  → 44px (w-11 h-11) — default: toolbars, action groups (WCAG 2.5.8)
 *
 * For borderless icon buttons use the CSS classes instead:
 *   .icon-button / .icon-button-admin / .icon-button-danger
 */

import React from 'react';
import { RefreshCw } from 'lucide-react';

const VARIANT_STYLES = {
  default: {
    base: 'border-gray-300 text-gray-600 hover:bg-gray-50',
    ring: 'focus-visible:ring-red-500',
  },
  primary: {
    base: 'border-red-200 text-red-600 hover:bg-red-50',
    ring: 'focus-visible:ring-red-500',
  },
  secondary: {
    base: 'border-red-200 text-red-600 hover:bg-red-50',
    ring: 'focus-visible:ring-red-500',
  },
  danger: {
    base: 'border-red-200 text-red-600 hover:bg-red-50',
    ring: 'focus-visible:ring-red-500',
  },
};

const SIZE_STYLES = {
  sm: { button: 'w-8 h-8', icon: 'w-3.5 h-3.5', text: 'text-xs' },
  md: { button: 'w-11 h-11', icon: 'w-4 h-4', text: 'text-sm' },
};

const IconButton = ({
  icon: Icon,
  onClick,
  disabled = false,
  loading = false,
  title,
  ariaLabel,
  variant = 'default',
  size = 'md',
  className = '',
  children,
  showLabel = false,
  labelMobile = null,
  ...props
}) => {
  const variantStyle = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const sizeStyle = SIZE_STYLES[size] || SIZE_STYLES.md;

  const displayLabel = showLabel ? (labelMobile || children) : null;
  const hasLabel = !!displayLabel;

  const buttonClasses = `
    ${hasLabel ? '' : sizeStyle.button}
    inline-flex items-center justify-center
    ${hasLabel ? 'px-3 py-2 h-auto' : ''}
    border rounded-full
    ${variantStyle.base}
    focus-visible:outline-none focus-visible:ring-2 ${variantStyle.ring} focus-visible:ring-offset-1
    transition-all duration-200
    ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  const finalAriaLabel = ariaLabel || title || (typeof children === 'string' ? children : 'Button');

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      aria-label={finalAriaLabel}
      className={buttonClasses}
      {...props}
    >
      {loading ? (
        <RefreshCw className={`${sizeStyle.icon} animate-spin`} />
      ) : (
        <>
          {Icon && <Icon className={sizeStyle.icon} />}
          {hasLabel && (
            <span className={`ml-2 ${sizeStyle.text} whitespace-nowrap`}>
              {displayLabel}
            </span>
          )}
        </>
      )}
    </button>
  );
};

export default IconButton;
